import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to scroll to top on route changes
 * @param {Object} options - Configuration options
 * @param {boolean} options.smooth - Whether to use smooth scrolling (default: false for better mobile performance)
 * @param {number} options.delay - Delay before scrolling (default: 0)
 * @param {Array} options.excludePaths - Paths to exclude from auto-scroll (default: [])
 */
export const useScrollToTop = (options = {}) => {
  const location = useLocation();
  const { 
    smooth = false, 
    delay = 0, 
    excludePaths = [] 
  } = options;

  useEffect(() => {
    // Check if current path should be excluded
    const shouldExclude = excludePaths.some(path => 
      location.pathname.startsWith(path)
    );

    if (shouldExclude) return;

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: smooth ? 'smooth' : 'auto'
      });
    };

    if (delay > 0) {
      const timeoutId = setTimeout(scrollToTop, delay);
      return () => clearTimeout(timeoutId);
    } else {
      scrollToTop();
    }
  }, [location.pathname, smooth, delay, excludePaths]);
};

/**
 * Hook specifically for mobile-optimized scroll to top
 * Uses instant scrolling for better performance on mobile devices
 */
export const useMobileScrollToTop = (excludePaths = []) => {
  const location = useLocation();

  useEffect(() => {
    // Default excluded paths for message-related pages
    const defaultExcludePaths = ['/messages'];
    const allExcludePaths = [...defaultExcludePaths, ...excludePaths];
    
    // Check if current path should be excluded
    const shouldExclude = allExcludePaths.some(path => 
      location.pathname.startsWith(path)
    );

    if (!shouldExclude) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
  }, [location.pathname, excludePaths]);
};

/**
 * Hook for smooth scroll to top (desktop-optimized)
 */
export const useSmoothScrollToTop = (excludePaths = []) => {
  return useScrollToTop({
    smooth: true,
    delay: 100, // Small delay to ensure page is rendered
    excludePaths
  });
};

export default useScrollToTop; 